/**
 * Start a super admin "View as user" session (TRUS-747).
 *
 * Asks for what the record needs: a classification, a written reason and the
 * mode. The Jira ticket is optional: left blank, the API creates one and
 * documents the session on it. Read-write takes an explicit acknowledgement
 * and then a second confirmation step. When opened without a user, it offers a
 * search across the org's staff and member-portal users and the platform admins.
 * The Start button says what is still missing instead of just greying out.
 *
 * Uses native <select> / radio inputs: the app's custom Select portals to
 * <body> and is not clickable inside a Radix Dialog.
 */
import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Eye, Loader2, PenLine, Search } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { authFetch } from '@/lib/authFetch';
import { startImpersonation, type ImpersonationMode } from '@/lib/impersonation';

export interface ViewAsTarget {
  id: number;
  name?: string | null;
  email?: string | null;
  kind?: 'staff' | 'member';
  role?: string | null;
  platform_admin?: boolean;
}

interface Options {
  classifications: { value: string; label: string }[];
  modes: { value: ImpersonationMode; label: string; minutes: number }[];
  min_reason_length: number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  cboId: number;
  orgName?: string;
  /** Pre-selected user (e.g. from a User Management row). */
  target?: ViewAsTarget | null;
}

const TICKET_RE = /^[A-Z][A-Z0-9]+-[0-9]+$/;
const fieldClass =
  'w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white';

export function ViewAsUserModal({ open, onClose, cboId, orgName, target: initialTarget }: Props) {
  const [options, setOptions] = useState<Options | null>(null);
  const [target, setTarget] = useState<ViewAsTarget | null>(initialTarget ?? null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ViewAsTarget[]>([]);
  const [orgOptedOut, setOrgOptedOut] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [ticketKey, setTicketKey] = useState('');
  const [classification, setClassification] = useState('');
  const [reason, setReason] = useState('');
  const [mode, setMode] = useState<ImpersonationMode>('read_only');
  const [acknowledged, setAcknowledged] = useState(false);
  const [confirmStep, setConfirmStep] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setTarget(initialTarget ?? null);
    setTicketKey('');
    setClassification('');
    setReason('');
    setMode('read_only');
    setAcknowledged(false);
    setConfirmStep(false);
    setError(null);
    setQuery('');
    authFetch('/api/impersonation/options')
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => data && setOptions(data))
      .catch(() => {});
  }, [open, initialTarget]);

  // User search (and the org's opt-out state) when no user was pre-selected.
  useEffect(() => {
    if (!open) return;
    const handle = setTimeout(async () => {
      setSearching(true);
      try {
        const params = new URLSearchParams({ cbo_id: String(cboId), limit: '25' });
        if (query.trim()) params.set('q', query.trim());
        const res = await authFetch(`/api/impersonation/targets?${params}`);
        if (res.ok) {
          const data = await res.json();
          setOrgOptedOut(!data.allow_support_impersonation);
          setResults(data.targets || []);
          setSearchError(null);
        } else {
          const body = await res.json().catch(() => null);
          setResults([]);
          setSearchError(
            typeof body?.detail === 'string' ? body.detail : `Could not load users (HTTP ${res.status}).`,
          );
        }
      } catch {
        setResults([]);
        setSearchError('Could not load users.');
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [open, cboId, query]);

  const minReason = options?.min_reason_length ?? 20;
  // Blank is fine: the API creates the Jira ticket.
  const ticketOk = !ticketKey.trim() || TICKET_RE.test(ticketKey.trim().toUpperCase());
  const reasonOk = reason.trim().length >= minReason;
  const missing: string[] = [];
  if (orgOptedOut) missing.push('this organization has turned View as user off');
  if (!target) missing.push('choose a user');
  if (!ticketOk) missing.push('fix the ticket key or leave it blank');
  if (!classification) missing.push('choose a classification');
  if (!reasonOk) missing.push(`write a reason of at least ${minReason} characters`);
  if (mode === 'read_write' && !acknowledged) missing.push('tick the read and write acknowledgement');
  const canSubmit = missing.length === 0;

  const minutes = useMemo(
    () => options?.modes.find((m) => m.value === mode)?.minutes ?? (mode === 'read_write' ? 30 : 60),
    [options, mode],
  );

  const submit = async () => {
    if (!target || !canSubmit) return;
    if (mode === 'read_write' && !confirmStep) {
      setConfirmStep(true);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await startImpersonation({
        targetUserId: target.id,
        cboId,
        ticketKey: ticketKey.trim().toUpperCase(),
        classification,
        reason: reason.trim(),
        mode,
        confirmReadWrite: mode === 'read_write' && acknowledged && confirmStep,
      });
      // startImpersonation navigates away on success.
    } catch (e: any) {
      setError(e?.message || 'Could not start the session.');
      setConfirmStep(false);
      setSubmitting(false);
    }
  };

  const who = target ? target.name || target.email || `User ${target.id}` : '';

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !submitting && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5 text-amber-600" />
            View as user
          </DialogTitle>
          <DialogDescription>
            Open {orgName ? `${orgName} ` : 'this organization '}as a specific person, with their data and their
            permissions. The person is told by email, and everything you do is recorded in WeaveIn and on the Jira
            ticket.
          </DialogDescription>
        </DialogHeader>

        {orgOptedOut && (
          <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            This organization has turned off View as user in Org Settings → Security.
          </div>
        )}

        {confirmStep ? (
          <div className="space-y-3 rounded-md border border-red-300 bg-red-50 p-4 text-sm text-red-900 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">
            <p className="font-semibold">Start a read and write session as {who}?</p>
            <p>
              Changes you make will be saved as {who}'s, with your name recorded next to each one. Messages, signatures,
              claims, payments, calls and password changes stay blocked. The session ends after {minutes} minutes.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Who */}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">User</label>
              {target ? (
                <div className="flex items-center justify-between rounded-md border border-gray-200 px-3 py-2 dark:border-gray-700">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-gray-900 dark:text-white">{who}</div>
                    <div className="truncate text-xs text-gray-500 dark:text-gray-400">
                      {target.email}
                      {target.kind ? ` · ${target.kind === 'member' ? 'Member portal' : target.role || 'Staff'}` : ''}
                    </div>
                  </div>
                  {!initialTarget && (
                    <button
                      type="button"
                      onClick={() => setTarget(null)}
                      className="text-xs font-medium text-blue-600 hover:underline dark:text-blue-400"
                    >
                      Change
                    </button>
                  )}
                </div>
              ) : (
                <div>
                  <div className="relative">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <input
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Search staff, portal members and platform admins"
                      className={`${fieldClass} pl-9`}
                    />
                  </div>
                  <div className="mt-2 max-h-48 overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700">
                    {searching && results.length === 0 ? (
                      <div className="flex items-center gap-2 px-3 py-2 text-sm text-gray-500">
                        <Loader2 className="h-4 w-4 animate-spin" /> Searching…
                      </div>
                    ) : searchError ? (
                      <div className="px-3 py-2 text-sm text-red-600 dark:text-red-400">{searchError}</div>
                    ) : results.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                        {query.trim()
                          ? `No one matches “${query.trim()}”. `
                          : 'No users to show. '}
                        The list holds active staff and member-portal users of {orgName || 'this organization'}, and
                        platform admins. Deactivated accounts and you yourself are not listed.
                      </div>
                    ) : (
                      results.map((r) => (
                        <button
                          key={`${r.kind}-${r.id}`}
                          type="button"
                          onClick={() => setTarget(r)}
                          className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          <span className="min-w-0">
                            <span className="block truncate font-medium text-gray-900 dark:text-white">
                              {r.name || r.email}
                            </span>
                            <span className="block truncate text-xs text-gray-500 dark:text-gray-400">{r.email}</span>
                          </span>
                          <span
                            className={`ml-2 shrink-0 rounded px-1.5 py-0.5 text-xs ${
                              r.platform_admin
                                ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300'
                                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                            }`}
                          >
                            {r.kind === 'member' ? 'Member' : r.platform_admin ? `Platform · ${r.role}` : r.role || 'Staff'}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Ticket + classification */}
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="viewas-ticket" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Jira ticket <span className="font-normal text-gray-500 dark:text-gray-400">(optional)</span>
                </label>
                <input
                  id="viewas-ticket"
                  value={ticketKey}
                  onChange={(e) => setTicketKey(e.target.value.toUpperCase())}
                  placeholder="TRUS-123"
                  className={fieldClass}
                  autoComplete="off"
                />
                {ticketKey && !ticketOk ? (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-400">Use the ticket key, e.g. TRUS-123.</p>
                ) : (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {ticketKey.trim()
                      ? 'Must exist in Jira. The session is added to it.'
                      : 'Leave blank to create a TRUS ticket for this session.'}
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="viewas-class" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Classification
                </label>
                <select
                  id="viewas-class"
                  value={classification}
                  onChange={(e) => setClassification(e.target.value)}
                  className={fieldClass}
                >
                  <option value="">Choose…</option>
                  {(options?.classifications || []).map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Reason */}
            <div>
              <label htmlFor="viewas-reason" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Reason
              </label>
              <textarea
                id="viewas-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder="What you need to see or fix, and why it has to be as this user."
                className={fieldClass}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {reason.trim().length}/{minReason} characters minimum. It goes into the Jira ticket, so do not include
                member health details.
              </p>
            </div>

            {/* Mode */}
            <fieldset>
              <legend className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Access</legend>
              <div className="grid gap-2 sm:grid-cols-2">
                <label
                  className={`flex cursor-pointer items-start gap-2 rounded-md border p-3 text-sm ${
                    mode === 'read_only'
                      ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20'
                      : 'border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <input
                    type="radio"
                    name="viewas-mode"
                    checked={mode === 'read_only'}
                    onChange={() => setMode('read_only')}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="flex items-center gap-1 font-medium text-gray-900 dark:text-white">
                      <Eye className="h-3.5 w-3.5" /> Read only
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">See everything, change nothing. 60 min.</span>
                  </span>
                </label>
                <label
                  className={`flex cursor-pointer items-start gap-2 rounded-md border p-3 text-sm ${
                    mode === 'read_write'
                      ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                      : 'border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <input
                    type="radio"
                    name="viewas-mode"
                    checked={mode === 'read_write'}
                    onChange={() => setMode('read_write')}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="flex items-center gap-1 font-medium text-gray-900 dark:text-white">
                      <PenLine className="h-3.5 w-3.5" /> Read and write
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">Make changes as them. 30 min.</span>
                  </span>
                </label>
              </div>
              {mode === 'read_write' && (
                <label className="mt-2 flex items-start gap-2 text-xs text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={acknowledged}
                    onChange={(e) => setAcknowledged(e.target.checked)}
                    className="mt-0.5"
                  />
                  I understand that changes are saved to this person's records as theirs, with my name recorded on each
                  one.
                </label>
              )}
            </fieldset>
          </div>
        )}

        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
            {error}
          </div>
        )}

        {!confirmStep && !canSubmit && (
          <p className="text-xs text-gray-500 dark:text-gray-400">To start: {missing.join('; ')}.</p>
        )}

        <DialogFooter className="gap-2">
          <button
            type="button"
            onClick={() => (confirmStep ? setConfirmStep(false) : onClose())}
            disabled={submitting}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
          >
            {confirmStep ? 'Back' : 'Cancel'}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit || submitting}
            className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-50 ${
              mode === 'read_write' ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'
            }`}
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {confirmStep ? 'Start read and write session' : mode === 'read_write' ? 'Continue' : 'Start viewing'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ViewAsUserModal;
